import React, { useEffect, useState } from 'lib/react';
import PropTypes from 'prop-types';
import {
  EVButton,
  EVClear,
  EVDialog,
  EVDialogActions,
  EVDialogContent,
  EVDialogTitle,
  EVTypography,
} from 'lib/components';
import LabelledCheckbox from 'commonComponents/labelled-checkbox';
import SitemapApprovalConfirmDialogStyles from './SitemapApprovalConfirmDialogStyles';

function SitemapApprovalConfirmDialog({
  open,
  reportId,
  address,
  orderPlacedDisplay,
  selectedCount,
  totalCount,
  submitting,
  onCancel,
  onConfirm,
}) {
  const classes = SitemapApprovalConfirmDialogStyles();
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (open) {
      setAcknowledged(false);
    }
  }, [open]);

  const handleClose = () => {
    if (!submitting) {
      onCancel();
    }
  };

  const billedCountLabel = selectedCount === 1 ? 'report' : 'reports';
  const acknowledgeLabel = [
    'Each selected structure will generate a measurement report to be processed.',
    'I acknowledge these are the structures I want measured',
    `and that I will be billed for ${selectedCount} separate ${billedCountLabel}.`,
  ].join(' ');

  return (
    <EVDialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.dialog }}
    >
      <EVDialogTitle className={classes.title}>
        Acknowledge order change
        <EVClear
          id="sitemap-approval-dialog-close"
          className={classes.closeButton}
          onClick={handleClose}
        />
      </EVDialogTitle>
      <EVDialogContent className={classes.content}>
        <EVTypography className={classes.bodyText}>
          {'You have selected '}
          <span className={classes.selectedCount}>
            {`${selectedCount}/${totalCount}`}
          </span>
          {' structures from your order:'}
        </EVTypography>
        <EVTypography className={classes.metaItem}>
          <span className={classes.metaLabel}>Report #: </span>
          {reportId}
        </EVTypography>
        <EVTypography className={classes.metaItem}>
          <span className={classes.metaLabel}>Address: </span>
          {address}
        </EVTypography>
        <EVTypography className={classes.metaItem}>
          <span className={classes.metaLabel}>Order placed on: </span>
          {orderPlacedDisplay}
        </EVTypography>
        <div className={classes.checkboxWrap}>
          <LabelledCheckbox
            checkboxId="sitemap-approval-acknowledge"
            checkboxLabel={acknowledgeLabel}
            checked={acknowledged}
            checkboxValue={acknowledged}
            handleCheckboxSelectChange={(_, checked) => setAcknowledged(checked)}
          />
        </div>
      </EVDialogContent>
      <EVDialogActions className={classes.actions}>
        <EVButton
          id="sitemap-approval-dialog-cancel"
          onClick={handleClose}
          color="primary"
          disabled={submitting}
        >
          Cancel
        </EVButton>
        <EVButton
          id="sitemap-approval-dialog-confirm"
          onClick={onConfirm}
          color="primary"
          variant="contained"
          disabled={!acknowledged || submitting}
        >
          {submitting ? 'Submitting…' : 'Confirm'}
        </EVButton>
      </EVDialogActions>
    </EVDialog>
  );
}

SitemapApprovalConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  address: PropTypes.string.isRequired,
  orderPlacedDisplay: PropTypes.string.isRequired,
  selectedCount: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  submitting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

SitemapApprovalConfirmDialog.defaultProps = {
  submitting: false,
};

export default SitemapApprovalConfirmDialog;
